/**
 * Parse yogaLesson.cues into ordered teach-wizard steps.
 *
 * Preferred format (rich):
 *   ## Step title · ~5 min
 *   Instructional body…
 *   **Pose:** mountain
 *   **Image:** https://… | Alt text
 *   **Say:** …
 *   **Tip:** …
 *   **Mod:** …
 *   **Breath:** …
 *
 *   ---
 *
 *   ## Next step
 *   …
 *
 * Legacy format (numbered one-liners) still works:
 *   1. Warm-up
 *   2. Standing sequence
 */

import { ensureStepImages, resolvePoseRef } from './poseImages';

function stripDecor(s) {
  return String(s || '')
    .replace(/^\*+\s*|\s*\*+$/g, '')
    .replace(/^_+\s*|\s*_+$/g, '')
    .trim();
}

function parseImageLine(val) {
  const raw = String(val || '').trim();
  if (!raw) return null;
  const pipe = raw.indexOf('|');
  if (pipe > 0) {
    const url = raw.slice(0, pipe).trim();
    const alt = raw.slice(pipe + 1).trim();
    return url ? { kind: 'img', url, alt: alt || 'Pose reference' } : null;
  }
  return { kind: 'img', url: raw, alt: 'Pose reference' };
}

function parseMetaLines(body) {
  const say = [];
  const tips = [];
  const mods = [];
  const breath = [];
  const images = [];
  const lines = [];

  for (const raw of String(body || '').split('\n')) {
    const line = raw.trim();
    if (!line) {
      if (lines.length && lines[lines.length - 1] !== '') lines.push('');
      continue;
    }

    // Markdown image: ![alt](url)
    const mdImg = line.match(/^!\[([^\]]*)\]\(([^)]+)\)\s*$/);
    if (mdImg) {
      images.push({
        kind: 'img',
        url: mdImg[2].trim(),
        alt: mdImg[1].trim() || 'Pose reference',
      });
      continue;
    }

    // Accept **Say:** value, **Say**: value, or Say: value
    const m = line.match(/^\*\*(Say|Tip|Mod|Breath|Cue|Time|Image|Pose|Photo):?\*\*\s*:?\s*(.+)$/i)
      || line.match(/^(Say|Tip|Mod|Breath|Cue|Time|Image|Pose|Photo)\s*:\s*(.+)$/i);
    if (m) {
      const kind = m[1].toLowerCase();
      const val = stripDecor(m[2]);
      if (kind === 'say' || kind === 'cue') say.push(val);
      else if (kind === 'tip') tips.push(val);
      else if (kind === 'mod') mods.push(val);
      else if (kind === 'breath') breath.push(val);
      else if (kind === 'image' || kind === 'photo') {
        const img = parseImageLine(val);
        if (img) images.push(img);
      } else if (kind === 'pose') {
        const img = resolvePoseRef(val);
        if (img) images.push(img);
      } else if (kind === 'time') {
        /* absorbed into title when present as meta */
      }
      continue;
    }
    lines.push(line);
  }

  // Trim trailing blank lines from body.
  while (lines.length && !lines[lines.length - 1]) lines.pop();

  return {
    body: lines.join('\n').trim(),
    say,
    tips,
    mods,
    breath,
    images,
  };
}

function emptyMeta() {
  return { body: '', say: [], tips: [], mods: [], breath: [], images: [] };
}

function splitRichBlocks(text) {
  const trimmed = String(text || '').trim();
  if (!trimmed) return [];

  // Explicit --- separators, or ## headings as block starts.
  if (/\n---\n/.test(`\n${trimmed}\n`) || /^##\s+/m.test(trimmed)) {
    let blocks;
    if (/\n---\n/.test(`\n${trimmed}\n`)) {
      blocks = trimmed.split(/\n---\n/).map((b) => b.trim()).filter(Boolean);
    } else {
      // Split before each ## heading, keeping the heading with its body.
      const parts = trimmed.split(/(?=^##\s+)/m).map((b) => b.trim()).filter(Boolean);
      blocks = parts;
    }
    return blocks.map((block, i) => {
      const lines = block.split('\n');
      let title = `Step ${i + 1}`;
      let rest = block;
      const head = lines[0] || '';
      if (/^##\s+/.test(head)) {
        title = head.replace(/^##\s+/, '').trim();
        rest = lines.slice(1).join('\n').trim();
      } else if (/^\d+\.\s+/.test(head)) {
        title = head.replace(/^\d+\.\s+/, '').trim();
        rest = lines.slice(1).join('\n').trim();
      }
      // Pull trailing "· ~N min" into a time label if present in title.
      let time = '';
      const timeInTitle = title.match(/^(.*?)\s*[·•|]\s*(~?\d+\s*min.*)$/i);
      if (timeInTitle) {
        title = timeInTitle[1].trim();
        time = timeInTitle[2].trim();
      }
      const meta = parseMetaLines(rest);
      return {
        title: title || `Step ${i + 1}`,
        time,
        ...meta,
      };
    });
  }
  return [];
}

function splitNumbered(text) {
  const trimmed = String(text || '').trim();
  if (!trimmed) return [];

  // Split on lines that start with "1. ", "2. ", etc.
  const parts = trimmed.split(/(?=^\d+\.\s+)/m).map((p) => p.trim()).filter(Boolean);
  if (parts.length < 2 && !/^\d+\.\s+/.test(trimmed)) return [];

  return parts.map((part, i) => {
    const lines = part.split('\n');
    const first = (lines[0] || '').replace(/^\d+\.\s+/, '').trim();
    const rest = lines.slice(1).join('\n').trim();
    let title = first;
    let time = '';
    const timeInTitle = title.match(/^(.*?)\s*[·•|]\s*(~?\d+\s*min.*)$/i);
    if (timeInTitle) {
      title = timeInTitle[1].trim();
      time = timeInTitle[2].trim();
    }
    // If the whole step is one line, use a short title + full line as body.
    if (!rest && first.length > 72) {
      const cut = first.indexOf('—') > 0 ? first.indexOf('—')
        : first.indexOf(' - ') > 0 ? first.indexOf(' - ')
          : first.indexOf('. ') > 0 ? first.indexOf('. ')
            : -1;
      if (cut > 12 && cut < 60) {
        return {
          title: first.slice(0, cut).trim(),
          time,
          ...emptyMeta(),
          body: first.slice(cut).replace(/^[\s—.\-]+/, '').trim(),
        };
      }
      return {
        title: `Step ${i + 1}`,
        time,
        ...emptyMeta(),
        body: first,
      };
    }
    const meta = parseMetaLines(rest);
    return {
      title: title || `Step ${i + 1}`,
      time,
      body: meta.body || (!rest ? '' : meta.body),
      // If no multi-line body, the first line IS the instruction.
      ...(rest ? meta : emptyMeta()),
      ...(!rest ? { body: first, title: title.length > 48 ? `Step ${i + 1}` : title } : {}),
    };
  }).map((step, i) => {
    // Clean up the one-liner case where title duplicated body.
    if (step.body === step.title) {
      return { ...step, title: `Step ${i + 1}` };
    }
    if (!step.body && step.title) {
      return { ...step, body: step.title, title: `Step ${i + 1}` };
    }
    return step;
  });
}

function splitPlainLines(text) {
  return String(text || '')
    .split(/\n+/)
    .map((l) => l.trim())
    .filter(Boolean)
    .map((line, i) => ({
      title: `Step ${i + 1}`,
      time: '',
      ...emptyMeta(),
      body: line.replace(/^\d+\.\s+/, ''),
    }));
}

/** @returns {{ title: string, time: string, body: string, say: string[], tips: string[], mods: string[], breath: string[], images: { url: string, alt: string }[] }[]} */
export function parseTeachSteps(cues) {
  const text = String(cues || '').trim();
  if (!text) return [];

  let steps;
  const rich = splitRichBlocks(text);
  if (rich.length) steps = rich;
  else {
    const numbered = splitNumbered(text);
    steps = numbered.length ? numbered : splitPlainLines(text);
  }

  // Stale lessons (pre-Pose pack) still get pose stills from title/body cues.
  return steps.map(ensureStepImages);
}

/**
 * Prefer a lesson’s stored `sequence`; fall back to parsing cue markup.
 * Practice / Teach should use `getLessonSequence` from lessonSequence.js —
 * this helper keeps older call sites working until they migrate.
 */
export function preferSequenceOrCues(lesson, cuesText) {
  if (Array.isArray(lesson?.sequence) && lesson.sequence.length) {
    return { kind: 'sequence', steps: lesson.sequence };
  }
  return { kind: 'cues', steps: parseTeachSteps(cuesText || lesson?.cues || '') };
}

/** Placeholder shown in the lesson form for authors. */
export const CUES_PLACEHOLDER =
  'One step per block. Use ## headings and --- between steps.\n\n' +
  '## Arrive & settle · ~3 min\n' +
  'Welcome the room. Soft gaze. Three belly breaths.\n' +
  '1. Sit comfortably near the front of the mat.\n' +
  '2. Soften jaw and shoulders.\n' +
  '**Pose:** easy\n' +
  '**Say:** "Feel the sit bones root; crown lifts."\n' +
  '**Tip:** Keep your voice warm and unhurried.\n' +
  '**Mod:** Chair seat is fine.\n\n' +
  '---\n\n' +
  '## Warm the spine · ~5 min\n' +
  '…';
